import type { BlockRegistry } from "./registry";
import type {
  AuthoredBlock,
  BlockResponse,
  GradeResult,
  LessonResult,
  ValidationResult,
} from "./types";

/**
 * Motor de evaluación. NO conoce ningún tipo concreto: para cada bloque busca
 * el plugin en el registro y delega. Añadir un tipo nuevo no toca este archivo.
 */

const UNKNOWN_TYPE_RESULT = (type: string, weight: number): GradeResult => ({
  correct: false,
  score: 0,
  maxScore: weight,
  feedback: `Tipo de bloque desconocido: "${type}"`,
});

function weightOf(block: AuthoredBlock): number {
  return block.weight != null && block.weight > 0 ? block.weight : 1;
}

/** Valida la definición de autoría de un bloque (para guardar en el editor). */
export function validateBlock(
  registry: BlockRegistry,
  block: AuthoredBlock,
): ValidationResult<unknown> {
  const plugin = registry.get(block.type);
  if (!plugin) {
    return { ok: false, errors: [`Tipo desconocido: "${block.type}"`] };
  }
  return plugin.validate(block.def);
}

/** Corrige un único bloque. Determinista; reproducible en servidor y cliente. */
export function gradeBlock(
  registry: BlockRegistry,
  block: AuthoredBlock,
  response: unknown,
): GradeResult {
  const weight = weightOf(block);
  const plugin = registry.get(block.type);
  if (!plugin) {
    return UNKNOWN_TYPE_RESULT(block.type, weight);
  }
  const validated = plugin.validate(block.def);
  if (!validated.ok) {
    return {
      correct: false,
      score: 0,
      maxScore: weight,
      feedback: `Definición inválida: ${validated.errors.join("; ")}`,
    };
  }
  const result = plugin.grade(validated.def, response);
  // El peso del bloque manda sobre el maxScore que declare el plugin.
  return { ...result, maxScore: weight };
}

export interface GradeLessonOpts {
  /** Umbral 0..1 para considerar la lección superada (default 0.8). */
  passThreshold?: number;
}

/**
 * Corrige una lección completa (lista de bloques) contra las respuestas del
 * alumno. Nota ponderada por `weight`. Los bloques `info` (peso 0 efectivo)
 * o sin nota se pueden excluir vía weight.
 */
export function gradeLesson(
  registry: BlockRegistry,
  blocks: readonly AuthoredBlock[],
  responses: readonly BlockResponse[],
  opts: GradeLessonOpts = {},
): LessonResult {
  const byId = new Map<string, unknown>();
  for (const r of responses) {
    byId.set(r.blockId, r.response);
  }
  const perBlock = new Map<string, GradeResult>();
  let earned = 0;
  let total = 0;
  for (const block of blocks) {
    const result = gradeBlock(registry, block, byId.get(block.id));
    perBlock.set(block.id, result);
    earned += result.score * result.maxScore;
    total += result.maxScore;
  }
  const score = total === 0 ? 0 : earned / total;
  const threshold = opts.passThreshold ?? 0.8;
  return { score, earned, total, passed: total > 0 && score >= threshold, perBlock };
}
